import { ChevronRight } from 'lucide-react';
import { useRef } from 'react';

import { useTheme } from '../../hooks/useTheme';

import type { NavSection } from './config';
import { NAV_THEMES } from './nav-tokens';
import NavSectionDot from './NavSectionDot';
import { sectionRowClass, sectionTone } from './navSectionRowClass';
import type { SectionBadge } from './section-badge';
import type { NavFlyoutApi } from './useNavFlyout';

interface NavSectionButtonProps {
  section: NavSection;
  collapsed: boolean;
  active: boolean;
  open: boolean;
  flyoutId: string;
  flyout: NavFlyoutApi;
  /** The dot, and the name and tooltip that explain it. */
  badge: SectionBadge;
}

/**
 * A section with pages: the row opens their fly-out and does not navigate.
 *
 * A mouse gets it on hover and leaving starts the close; a tap arrives as a
 * click instead, so a touch device reaches the same fly-out without a hover it
 * can never perform. The pointer handlers ignore anything but a mouse, or a
 * tap would open on its own enter event and then be toggled shut by the click
 * behind it.
 */
export default function NavSectionButton({
  section,
  collapsed,
  active,
  open,
  flyoutId,
  flyout,
  badge,
}: NavSectionButtonProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const ref = useRef<HTMLButtonElement | null>(null);
  const Icon = section.icon;

  return (
    <button
      ref={ref}
      type="button"
      aria-expanded={open}
      aria-controls={open ? flyoutId : undefined}
      // The rail can only say which section you are in; the page you are on is
      // marked inside the fly-out.
      aria-current={active ? 'true' : undefined}
      aria-label={badge.name}
      title={badge.name}
      // `detail === 0` is a click the keyboard produced, which is the one case
      // that should pull focus into the fly-out.
      onClick={(e) => {
        if (ref.current) flyout.toggle(section.id, ref.current, e.detail === 0);
      }}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse' && ref.current) {
          flyout.show(section.id, ref.current, false);
        }
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') flyout.scheduleClose();
      }}
      className={`${sectionRowClass(collapsed)} relative cursor-pointer`}
      style={sectionTone(t, active || open)}
    >
      <Icon size={18} aria-hidden className="shrink-0" />
      {badge.has && collapsed && <NavSectionDot className="absolute right-1 top-1" />}
      {!collapsed && (
        <>
          <span>{section.label}</span>
          {badge.has && <NavSectionDot />}
          <span className="ml-auto" aria-hidden>
            <ChevronRight size={18} />
          </span>
        </>
      )}
    </button>
  );
}
